import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { useSelector } from "react-redux";

import AdminSearchForm from "../../forms/AdminSearchForm";
import CustomButton from "../../../app/common/form/CustomButton";
import { clearSystemNoticesCache } from "../../../app/common/systemNotices/systemNoticesApi";

function toLocalInputValue(d) {
    const pad = (n) => String(n).padStart(2, "0");

    return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        "T" +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
    );
}

function formatLocalDateTime(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
}

function toIsoString(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString();
}

const NOTICE_TYPES = [
    { key: "maintenance", label: "Maintenance Lock" },
    { key: "banner", label: "Banner" },
    { key: "toast", label: "Toast" },
];

export default function SystemSettings(props) {
    const { currentUser } = useSelector((state) => state.auth);

    const isAdmin = !!(currentUser && currentUser.isAdmin === 1);
    const token = currentUser?.token;

    const [type, setType] = useState("maintenance");
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const headers = useMemo(() => {
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }, [token]);

    async function loadNotices(activeType) {
        if (!isAdmin || !token) return;

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const res = await axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}system-notices?type=${encodeURIComponent(activeType)}&per_page=20`,
                headers,
            });

            setItems(res.data?.data || []);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Failed to load notices");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadNotices(type);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, isAdmin, token]);

    async function toggleNotice(id, enabled) {
        setError("");
        setSuccess("");

        try {
            await axios({
                method: "put",
                url: `${process.env.REACT_APP_API_URL}system-notices/${id}`,
                headers,
                data: { enabled },
            });

            clearSystemNoticesCache();
            setSuccess("Updated.");
            loadNotices(type);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || "Update failed");
        }
    }

    const pathname = props?.location?.pathname || "/system-settings";

    const now = new Date();
    const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);

    const initialValues = {
        type,
        severity: type === "maintenance" ? "" : "info",
        message: "",
        starts_at: toLocalInputValue(now),
        ends_at: toLocalInputValue(inOneHour),
        enabled: true,
        toast_timeout_seconds: 8,
    };

    if (!isAdmin) {
        return (
            <div className="container mx-auto pt-10 px-4">
                <p className="text-lk_red">Admins only.</p>
            </div>
        );
    }

    return (
        <>
            <div className="bg-gray-50">
                <AdminSearchForm
                    heading="System Settings"
                    color="text-gray-500"
                    header_color="bg-gradient-to-b from-gray-300"
                    crumb_color="text-black"
                    breadcrumbs={[
                        { name: "Home", href: "/", count: 1 },
                        { name: "System Settings", href: pathname, count: 2 },
                    ]}
                />

                <div className="container mx-auto pt-6 px-4 pb-10">
                    <div className="max-w-4xl">
                        <h2 className="text-2xl font-semibold mb-2">System Notices</h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Maintenance locks block customer access (API returns 503). Admins can still log in and use the site.
                        </p>

                        <div className="flex flex-wrap gap-2 mb-6">
                            {NOTICE_TYPES.map((t) => (
                                <button
                                    key={t.key}
                                    className={`px-4 py-2 rounded ${type === t.key ? "bg-gray-800 text-white" : "bg-gray-100"}`}
                                    onClick={() => setType(t.key)}
                                    type="button"
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        {error ? <p className="text-lk_red mb-4">{error}</p> : null}
                        {success ? <p className="text-green-700 mb-4">{success}</p> : null}

                        <Formik
                            key={type}
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validate={(values) => {
                                const errors = {};
                                if (!values.message) errors.message = "Message is required";
                                if (!values.starts_at) errors.starts_at = "From date is required";
                                if (!values.ends_at) errors.ends_at = "To date is required";
                                if (values.starts_at && values.ends_at) {
                                    const a = new Date(values.starts_at);
                                    const b = new Date(values.ends_at);
                                    if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime()) && b.getTime() <= a.getTime()) {
                                        errors.ends_at = "To must be after From";
                                    }
                                }
                                return errors;
                            }}
                            onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                                setError("");
                                setSuccess("");

                                try {
                                    const payload = {
                                        type,
                                        severity: type === "maintenance" ? null : values.severity,
                                        message: values.message,
                                        starts_at: values.starts_at,
                                        ends_at: values.ends_at,
                                        enabled: !!values.enabled,
                                        toast_timeout_seconds: type === "toast" ? Number(values.toast_timeout_seconds || 8) : null,
                                    };

                                    await axios({
                                        method: "post",
                                        url: `${process.env.REACT_APP_API_URL}system-notices`,
                                        headers,
                                        data: payload,
                                    });

                                    clearSystemNoticesCache();
                                    setSuccess("Saved.");
                                    resetForm();
                                    loadNotices(type);
                                } catch (e) {
                                    const msg = e?.response?.data?.message || e?.message || "Save failed";
                                    setErrors({ message: msg });
                                    setError(msg);
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({ isSubmitting, values, errors, setFieldValue }) => (
                                <Form className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Message</label>
                                        <textarea
                                            className="w-full border rounded p-2"
                                            rows={4}
                                            value={values.message}
                                            onChange={(e) => setFieldValue("message", e.target.value)}
                                        />
                                        {errors.message ? <p className="text-lk_red text-sm">{errors.message}</p> : null}
                                    </div>

                                    {type !== "maintenance" ? (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Severity</label>
                                            <select
                                                className="w-full border rounded p-2"
                                                value={values.severity}
                                                onChange={(e) => setFieldValue("severity", e.target.value)}
                                            >
                                                <option value="info">Info</option>
                                                <option value="warning">Warning</option>
                                                <option value="danger">Danger</option>
                                            </select>
                                        </div>
                                    ) : null}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">From</label>
                                            <input
                                                type="datetime-local"
                                                className="w-full border rounded p-2"
                                                value={values.starts_at}
                                                onChange={(e) => setFieldValue("starts_at", e.target.value)}
                                            />
                                            {errors.starts_at ? <p className="text-lk_red text-sm">{errors.starts_at}</p> : null}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">To</label>
                                            <input
                                                type="datetime-local"
                                                className="w-full border rounded p-2"
                                                value={values.ends_at}
                                                onChange={(e) => setFieldValue("ends_at", e.target.value)}
                                            />
                                            {errors.ends_at ? <p className="text-lk_red text-sm">{errors.ends_at}</p> : null}
                                        </div>
                                    </div>

                                    {type === "toast" ? (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Toast timeout (seconds)</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={600}
                                                className="w-full border rounded p-2"
                                                value={values.toast_timeout_seconds}
                                                onChange={(e) => setFieldValue("toast_timeout_seconds", e.target.value)}
                                            />
                                        </div>
                                    ) : null}

                                    <div className="flex items-center gap-2">
                                        <input
                                            id="enabled"
                                            type="checkbox"
                                            checked={!!values.enabled}
                                            onChange={(e) => setFieldValue("enabled", e.target.checked)}
                                        />
                                        <label htmlFor="enabled" className="text-sm">
                                            Enabled
                                        </label>
                                    </div>

                                    <CustomButton
                                        disabled={isSubmitting}
                                        submitBool={isSubmitting}
                                        type="submit"
                                        text="Create Notice"
                                        className="lkbutton bg-lk_red hover:bg-lk_red_hover w-full border_radius_10"
                                    />
                                </Form>
                            )}
                        </Formik>

                        <div className="mt-10">
                            <h3 className="text-xl font-semibold mb-2">Recent {type} notices</h3>
                            {loading ? <p>Loading...</p> : null}

                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="text-left p-2">From</th>
                                            <th className="text-left p-2">To</th>
                                            <th className="text-left p-2">Enabled</th>
                                            <th className="text-left p-2">Message</th>
                                            <th className="text-left p-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(items || []).map((n) => (
                                            <tr key={n.id} className="border-b">
                                                <td className="p-2">{formatLocalDateTime(n.starts_at)}</td>
                                                <td className="p-2">{formatLocalDateTime(n.ends_at)}</td>
                                                <td className="p-2">{n.enabled ? "Yes" : "No"}</td>
                                                <td className="p-2">{(n.message || "").slice(0, 120)}</td>
                                                <td className="p-2">
                                                    {n.enabled ? (
                                                        <button
                                                            type="button"
                                                            className="text-red-600 underline"
                                                            onClick={() => toggleNotice(n.id, false)}
                                                        >
                                                            Disable
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="text-blue-600 underline"
                                                            onClick={() => toggleNotice(n.id, true)}
                                                        >
                                                            Enable
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


